import { RouteFactory } from "@app/routePaths";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import BasicCard from "@components/ArticleCard/BasicCard";
import { CardVariations } from "@components/ArticleCard/types";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import { useHandleMoreStories } from "@hooks/useHandleMoreStories";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { SanitizedSectionArticle } from "@typings/Cue";
import { cn, getFormattedUrlPath } from "@util/helpers";

type MoreStoriesProps = {
  /** This is the classname of the component. */
  className?: string;
  /** List of articles to be displayed. */
  articles: SanitizedSectionArticle[];
};

/** The more stories component of BT SG60 page.
 * https://www.figma.com/design/50gwyNfDsv70dHaddmTd7k/BT-SG60--WEB---APP-?node-id=164-79153&m=dev
 */
const MoreStories = ({
  className,
  articles,
}: MoreStoriesProps): React.ReactElement => {
  const OktaUserInfo = useOKTAUserStore((store) => store.userInfo);
  const isSubscriber = OktaUserInfo?.usertype === OKTAUserTypeEnum.SUBSCRIBER;
  const { ref, loadMoreData } = useHandleMoreStories({
    data: articles,
    uniqueCategoryQuery: RouteFactory.btsg60.replace("/keywords/", ""),
    parentCategory: "keywords",
    pageSize: 10,
  });

  if (articles.length === 0) return <></>;

  const variant: CardVariations = {
    kicker: { color: cn("text-gray-515") },
    image: {
      width: cn("w-1/4"),
      position: "left",
      extraClass: cn("lg:order-2"),
      filters: [
        { breakpoint: "(min-width: 992px)", w: 400, h: 267, dpr: 1, f: "webp" },
        { w: 300, h: 200, dpr: 1, f: "webp" },
      ],
    },
    content: {
      width: cn("w-3/4"),
      extraClass: cn("lg:order-1 pl-4 lg:pl-0 lg:pr-4 self-stretch"),
      blurb: { extraClass: "text-gray-850 mb-0" },
    },
    title: { size: "text-base", color: "text-gray-850" },
  };

  return (
    <div data-testid="more-stories-component" className={className}>
      <Row>
        <Column rootClassName={cn("w-full mb-8 lg:w-2/3")}>
          <BlockTitle
            text="MORE STORIES"
            rootClassName="mb-4 border-t border-gray-850 pt-3"
          />

          <div className="grid gap-8">
            {[...articles, ...loadMoreData].map(
              ({
                id,
                title,
                kicker,
                media,
                blurb,
                urlPath,
                created,
                edited,
                updated,
                displaySetting,
                sections,
                paywall,
              }) => {
                const defaultImage = {
                  directoryName: sections?.[0]?.directoryName || "",
                };
                const link = getFormattedUrlPath(urlPath, [
                  { key: "ref", value: "btsg60-more-stories" },
                ]);

                return (
                  <BasicCard
                    key={id}
                    id={id}
                    title={displaySetting?.displayHeadline || title}
                    kicker={kicker?.fields[0]?.value}
                    media={media}
                    blurb={blurb}
                    slug={link}
                    variations={variant}
                    created={created}
                    edited={edited}
                    updated={updated}
                    hasUpdatedTimestampDisplay={
                      displaySetting?.hasUpdatedTimeDisplayed
                    }
                    paywall={
                      isSubscriber ? paywall.contentAccess === "1" : undefined
                    }
                    defaultImage={defaultImage}
                    rootClassName={cn(
                      "relative after:absolute after:-bottom-4 after:w-full after:h-[1px] after:bg-gray-175"
                    )}
                  />
                );
              }
            )}
          </div>

          <div ref={ref} />
        </Column>

        <Column rootClassName={cn("w-full mb-8 lg:w-1/3")}>
          <Ad
            adType="imu2"
            className="flex flex-col items-center justify-center lg:!sticky lg:top-18"
          />
        </Column>
      </Row>
    </div>
  );
};

export default MoreStories;
